import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, Plus, Minus, Send, User, Phone, Mail, MessageSquare, CheckCircle } from 'lucide-react';
import { sendOrderToWhatsApp } from '../services/whatsapp';
import { Order } from '../types';

export const OrderForm: React.FC = () => {
  const [formData, setFormData] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    quantity: 1,
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleQuantityChange = (change: number) => {
    setFormData(prev => ({
      ...prev,
      quantity: Math.max(1, Math.min(99, prev.quantity + change))
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const order: Order = {
      id: `ORDER-${Date.now()}`,
      ...formData,
      timestamp: new Date(),
      status: 'pending'
    };

    // Send order to WhatsApp
    sendOrderToWhatsApp(order);

    // Show success state
    setTimeout(() => {
      setIsSubmitted(true);
      setIsSubmitting(false);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          customerName: '',
          customerPhone: '',
          customerEmail: '',
          quantity: 1,
          message: ''
        });
        setIsSubmitted(false);
      }, 3000);
    }, 1000);
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="card p-12 max-w-md w-full mx-auto text-center"
      >
        <motion.div
          className="w-20 h-20 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-6"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <CheckCircle className="w-10 h-10 text-success" />
        </motion.div>
        <h3 className="text-2xl font-bold gradient-text mb-4">Order Sent Successfully! 🎉</h3>
        <p className="text-gray-600 mb-6">Your order has been sent to us via WhatsApp. We'll contact you soon to confirm your order.</p>
        <div className="text-sm text-gray-500">Redirecting to form...</div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="card p-8 max-w-lg w-full mx-auto"
    >
      {/* Header */}
      <div className="text-center mb-8">
        <motion.div
          className="w-16 h-16 bg-gradient-to-r from-ginger to-ginger-dark rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"
          whileHover={{ scale: 1.1, rotate: 10 }}
        >
          <ShoppingCart className="w-8 h-8 text-white" />
        </motion.div>
        <h2 className="text-3xl font-bold gradient-text mb-2">Place Your Order</h2>
        <p className="text-gray-600">Fresh homemade ginger garlic paste delivered to your doorstep</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name Field */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
            <User className="w-4 h-4 mr-2 text-ginger" />
            Full Name *
          </label>
          <input
            type="text"
            name="customerName"
            value={formData.customerName}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ginger focus:border-transparent transition-all duration-300 bg-white/70 backdrop-blur-sm"
            placeholder="Enter your full name"
          />
        </motion.div>

        {/* Phone Field */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
            <Phone className="w-4 h-4 mr-2 text-ginger" />
            Phone Number *
          </label>
          <input
            type="tel"
            name="customerPhone"
            value={formData.customerPhone}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ginger focus:border-transparent transition-all duration-300 bg-white/70 backdrop-blur-sm"
            placeholder="Enter your phone number"
          />
        </motion.div>

        {/* Email Field */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
        >
          <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
            <Mail className="w-4 h-4 mr-2 text-ginger" />
            Email Address *
          </label>
          <input
            type="email"
            name="customerEmail"
            value={formData.customerEmail}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ginger focus:border-transparent transition-all duration-300 bg-white/70 backdrop-blur-sm"
            placeholder="Enter your email address"
          />
        </motion.div>

        {/* Quantity Field */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
        >
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Quantity (kgs) *
          </label>
          <div className="flex items-center justify-center space-x-4">
            <motion.button
              type="button"
              onClick={() => handleQuantityChange(-1)}
              className="w-12 h-12 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center border-2 border-gray-200 hover:border-ginger"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <Minus className="w-5 h-5 text-gray-600" />
            </motion.button>
            
            <div className="text-center">
              <input
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleInputChange}
                min="1"
                max="99"
                required
                className="w-24 px-4 py-3 text-center text-2xl font-bold border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ginger focus:border-transparent transition-all duration-300 bg-white/70 backdrop-blur-sm"
              />
              <p className="text-sm text-gray-500 mt-1">kgs</p>
            </div>
            
            <motion.button
              type="button"
              onClick={() => handleQuantityChange(1)}
              className="w-12 h-12 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center border-2 border-gray-200 hover:border-ginger"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <Plus className="w-5 h-5 text-gray-600" />
            </motion.button>
          </div>
        </motion.div>

        {/* Message Field */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
        >
          <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
            <MessageSquare className="w-4 h-4 mr-2 text-ginger" />
            Additional Message (Optional)
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-ginger focus:border-transparent transition-all duration-300 bg-white/70 backdrop-blur-sm resize-none"
            placeholder="Any special instructions or delivery notes..."
          />
        </motion.div>

        {/* Submit Button */}
        <motion.button
          type="submit"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          disabled={isSubmitting}
          className="w-full btn-primary text-lg shadow-glow-lg flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <motion.div
                className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              />
              <span>Sending Order...</span>
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              <span>Send Order</span>
            </>
          )}
        </motion.button>
      </form>

      {/* Trust Indicators */}
      <motion.div
        className="mt-6 pt-6 border-t border-gray-200"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        <div className="flex justify-center space-x-6 text-xs text-gray-500">
          <div className="flex items-center">
            <CheckCircle className="w-3 h-3 mr-1 text-success" />
            <span>Secure</span>
          </div>
          <div className="flex items-center">
            <CheckCircle className="w-3 h-3 mr-1 text-success" />
            <span>Fast Delivery</span>
          </div>
          <div className="flex items-center">
            <CheckCircle className="w-3 h-3 mr-1 text-success" />
            <span>Fresh Product</span>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
